import React, { ChangeEvent, memo, useState } from 'react'
import { Button } from 'transfix-ui/components/Button'
import { ButtonGroup } from 'transfix-ui/components/ButtonGroup'
import { useDebouncedCallback } from 'use-debounce'
import ShipperAutosuggest, { IShipperResult } from 'apps/marketplace/components/ShipperAutosuggest'
import { useMPAnalytics } from 'apps/marketplace/hooks'
import { IRFPLaneSpecificFilters } from 'apps/marketplace/utils/api/rfpLanes'
import { TextInput } from 'components/atoms'
import { LaneArrowIcon } from 'transfix-icons'
import LaneLocationSearch, {
  ILaneLocationSearchValues,
} from '../../lane/assignment/LaneLocationSearch'

interface ISearchRFPFiltersProps {
  filters: IRFPLaneSpecificFilters
  setFilters: (value: IRFPLaneSpecificFilters) => void
  clearFilters: () => void
  className?: string
}

enum RFP_SEARCH_TYPE {
  id = 'id',
  more = 'more',
}

export const SearchRFPFilters = memo(
  ({ className, clearFilters, filters, setFilters }: ISearchRFPFiltersProps) => {
    const { trackEvent } = useMPAnalytics()
    const [rfpSearchType, setRfpSearchType] = useState<RFP_SEARCH_TYPE | undefined>(
      RFP_SEARCH_TYPE.more
    )
    const [shipperApplication, setShipperApplication] = useState<
      Partial<IShipperResult> | undefined
    >(undefined)

    const onButtonGroupChange = (value: string) => {
      setRfpSearchType(value as RFP_SEARCH_TYPE)
      trackEvent('RFP Lanes', 'FILTER_BY_RFP_LANE', { searchType: value })
      clearFilters()
    }

    const onShipperChange = (shipper?: Partial<IShipperResult>) => {
      setShipperApplication(shipper)
      trackEvent('RFP Lanes', 'FILTER_BY_RFP_LANE', {
        shipperApplicationId: shipper?.id,
      })
      setFilters({ shipperApplicationId: shipper?.id })
    }

    const onRFPLaneIdChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      trackEvent('RFP Lanes', 'FILTER_BY_RFP_LANE', {
        id: value,
      })
      setFilters({ id: value })
    }
    const [debounceRfpLaneIdChange] = useDebouncedCallback(onRFPLaneIdChange, 250)

    const onLocationChange = (type: string, locationData?: ILaneLocationSearchValues) => {
      const locationChange = {
        [`${type}City`]: locationData?.city,
        [`${type}StateCode`]: locationData?.stateCode,
      }
      trackEvent('RFP Lanes', 'FILTER_BY_RFP_LANE', {
        ...locationChange,
      })
      setFilters({ ...locationChange })
    }

    const clearAllFilters = () => {
      clearFilters()
    }

    const { deliveryCity, id, pickupCity } = filters || {}

    return (
      <div className={className}>
        <>
          <div className='flex'>
            <div className='w-20'>
              <ButtonGroup
                className='dialog-medium w-100 mr2'
                value={rfpSearchType}
                hideLabel
                onChange={onButtonGroupChange}
                dataTest='assign-driver'>
                <Button value={RFP_SEARCH_TYPE.id} size='large' data-test='id-search'>
                  ID Only
                </Button>
                <Button value={RFP_SEARCH_TYPE.more} size='large' data-test='more-search'>
                  More Filters
                </Button>
              </ButtonGroup>
            </div>
            {rfpSearchType && (id || pickupCity || deliveryCity) && (
              <div className='w-auto'>
                <Button
                  className='btn-tertiary ml3'
                  size='large'
                  onClick={clearAllFilters}
                  data-test='clear-all'>
                  Clear All
                </Button>
              </div>
            )}
          </div>
          {rfpSearchType === RFP_SEARCH_TYPE.id && (
            <div className='flex mv2'>
              <div className='w-sixth'>
                <TextInput
                  onChange={debounceRfpLaneIdChange}
                  label='RFP Lane ID'
                  placeholder='Enter RFP Lane ID'
                  dataTest='search-rfp-lane-id'
                  clearable={false}
                  value={id}
                />
              </div>
            </div>
          )}
          {rfpSearchType === RFP_SEARCH_TYPE.more && (
            <div className='flex mv2'>
              <div className='w-sixth'>
                <LaneLocationSearch
                  placeholder='Enter city name'
                  id='pickup'
                  dataTest='set-pickup'
                  onChange={onLocationChange}
                  hasValue={!!pickupCity}
                  isLarge={false}
                  label='Pickup City'
                />
              </div>
              <div className='w-auto'>
                <LaneArrowIcon size='large' className='inline ml3 mt5' />
              </div>
              <div className='w-sixth ml3'>
                <LaneLocationSearch
                  placeholder='Enter city name'
                  id='delivery'
                  dataTest='set-delivery'
                  onChange={onLocationChange}
                  hasValue={!!deliveryCity}
                  isLarge={false}
                  label='Delivery City'
                />
              </div>
              <div className='w-sixth ml3'>
                <ShipperAutosuggest
                  value={shipperApplication}
                  onChange={onShipperChange}
                  dataTest='set-shipper'
                  confirmed={false}
                />
              </div>
            </div>
          )}
        </>
      </div>
    )
  }
)
